import React, { useState, useEffect, useMemo } from "react";

import {
  Mail,
  Phone,
  Calendar,
  Eye,
  MapPin,
  Trophy,
  Users,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import AssignedAccountsModal from "./AssignedAccountsModal";
import ExecutiveAccountsModal from "./ExecutiveAccountsModal";
import DropdownSelector from "../../Components/DropdownSelector";
import PageHeader from "../../Components/PageHeader";
import SearchInput from "../../Components/Search/SearchInput";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchExecutiveSponsors,
  Executive,
} from "../../Store/Reducers/OpsSlice";
import { fetchVendorAdminGrades } from "../../Store/Reducers/VendorAdminSlice";
import {
  ROLES,
  ROLE_DISPLAY_NAMES,
  isRole,
} from "../../Utils/Constants/RoleConstants";
import {
  getTierBadgeClasses,
  tierBadgeLayoutClasses,
  useAccountTiers,
} from "../../Utils/Helper/accountTierHelpers";
import { getInitials, formatDateUS } from "../../Utils/Helper/Helper";

const ROLE_FILTER_ALL = "all";

const EXECUTIVE_STAFF_ROLE_FILTERS = [
  ROLES.EXECUTIVE_SPONSOR_LEAD,
  ROLES.EXECUTIVE_SPONSOR,
  ROLES.EXECUTIVE,
] as const;

type ExecutiveStaffRoleFilter =
  | typeof ROLE_FILTER_ALL
  | (typeof EXECUTIVE_STAFF_ROLE_FILTERS)[number];

const parseRoleFilterParam = (
  roleParam?: string | null
): ExecutiveStaffRoleFilter => {
  const normalized = (roleParam || "").toLowerCase().trim();
  if (!normalized || normalized === ROLE_FILTER_ALL) {
    return ROLE_FILTER_ALL;
  }
  // Legacy short aliases from older links
  if (normalized === "lead") {
    return ROLES.EXECUTIVE_SPONSOR_LEAD;
  }
  if (normalized === "sponsor") {
    return ROLES.EXECUTIVE_SPONSOR;
  }
  const match = EXECUTIVE_STAFF_ROLE_FILTERS.find(role => role === normalized);
  return match ?? ROLE_FILTER_ALL;
};

type ExecutiveSortField = "name" | "performance" | "utilization" | "grade";
type SortDirection = "asc" | "desc";

interface ExecutiveSortOption {
  label: string;
  value: string;
  field: ExecutiveSortField;
  direction: SortDirection;
}

/** Executive-role cards show "NA" for performance, utilization and load. */
const isExecutiveOnlyRole = (executive: Executive): boolean =>
  isRole(executive.role, ROLES.EXECUTIVE);

/** Same value the card shows under "Performance". */
const getPerformanceValue = (executive: Executive): number =>
  executive.performanceScore ?? executive.current_accounts ?? 0;

const NA_LAST_SORT_FIELDS: ExecutiveSortField[] = [
  "performance",
  "utilization",
];

const EXECUTIVE_SORT_COMPARATORS: Record<
  ExecutiveSortField,
  (a: Executive, b: Executive) => number
> = {
  name: (a, b) =>
    (a.name?.toLowerCase() || "").localeCompare(b.name?.toLowerCase() || ""),
  performance: (a, b) => getPerformanceValue(a) - getPerformanceValue(b),
  utilization: (a, b) => (a.utilization || 0) - (b.utilization || 0),
  grade: (a, b) => (a.grade || 0) - (b.grade || 0),
};

const createSortOption = (
  label: string,
  field: ExecutiveSortField,
  direction: SortDirection
): ExecutiveSortOption => ({
  label,
  value: `${field}_${direction}`,
  field,
  direction,
});

const EXECUTIVE_SORT_OPTIONS: ExecutiveSortOption[] = [
  createSortOption("Name A-Z", "name", "asc"),
  createSortOption("Name Z-A", "name", "desc"),
  createSortOption("Performance High to Low", "performance", "desc"),
  createSortOption("Performance Low to High", "performance", "asc"),
  createSortOption("Utilization High to Low", "utilization", "desc"),
  createSortOption("Utilization Low to High", "utilization", "asc"),
  createSortOption("Grade High to Low", "grade", "desc"),
  createSortOption("Grade Low to High", "grade", "asc"),
];

const DEFAULT_EXECUTIVE_SORT = EXECUTIVE_SORT_OPTIONS[0];

const ExecutiveStaff: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: apiData,
    loading,
    error,
  } = useAppSelector(state => state.ops.executiveSponsors);
  const { grades } = useAppSelector(state => state.vendorAdmin);
  const { formatTier } = useAccountTiers();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null);
  const [selectedRoleFilter, setSelectedRoleFilter] =
    useState<ExecutiveStaffRoleFilter>(() =>
      parseRoleFilterParam(searchParams.get("role"))
    );
  const [selectedSort, setSelectedSort] = useState<string>(
    DEFAULT_EXECUTIVE_SORT.value
  );
  const [selectedExecutive, setSelectedExecutive] = useState<Executive | null>(
    null
  );
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAccountsModalOpen, setIsAccountsModalOpen] = useState(false);

  // Fetch executive sponsors and grades on mount
  useEffect(() => {
    dispatch(fetchExecutiveSponsors({ include_exec_leads: true }));
    dispatch(fetchVendorAdminGrades());
  }, [dispatch]);

  // Keep role filter in sync when landing with ?role=... from KPI tip link
  useEffect(() => {
    setSelectedRoleFilter(parseRoleFilterParam(searchParams.get("role")));
  }, [searchParams]);

  const handleRoleFilterChange = (value: string) => {
    const next = parseRoleFilterParam(value);
    setSelectedRoleFilter(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next === ROLE_FILTER_ALL) {
      nextParams.delete("role");
    } else {
      nextParams.set("role", next);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const roleFilterOptions = useMemo(
    () => [
      { label: "All Executives", value: ROLE_FILTER_ALL },
      ...EXECUTIVE_STAFF_ROLE_FILTERS.map(role => ({
        label: ROLE_DISPLAY_NAMES[role],
        value: role,
      })),
    ],
    []
  );

  // Calculate statistics from API data
  const cardData = useMemo(() => {
    if (!apiData) {
      return [
        { label: "Total Executives", value: 0 },
        { label: "Active", value: 0 },
        { label: "Bench", value: 0 },
        { label: "Avg Utilization", value: "0%" },
      ];
    }

    const stats = apiData.statistics;

    return [
      { label: "Total Executives", value: stats.total_executive_sponsors },
      { label: "Active", value: stats.executive_sponsors_assigned_to_accounts },
      {
        label: "Bench",
        value: stats.executive_sponsors_not_assigned_to_accounts,
      },
      {
        label: "Avg Utilization",
        value: `${stats.percentage_assigned_to_accounts}%`,
      },
    ];
  }, [apiData]);

  // Filter and sort executives
  const filteredExecutives = useMemo(() => {
    if (!apiData?.executives) return [];

    let filtered = [...apiData.executives];

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(exec =>
        exec.name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply grade filter
    if (selectedGrade && selectedGrade !== "all") {
      filtered = filtered.filter(
        exec => exec.experienceLevel === selectedGrade
      );
    }

    // Apply role filter using ROLES constants
    if (selectedRoleFilter !== ROLE_FILTER_ALL) {
      filtered = filtered.filter(exec => isRole(exec.role, selectedRoleFilter));
    }

    // Apply sorting
    const { field, direction } =
      EXECUTIVE_SORT_OPTIONS.find(option => option.value === selectedSort) ??
      DEFAULT_EXECUTIVE_SORT;
    const multiplier = direction === "asc" ? 1 : -1;
    const compare = EXECUTIVE_SORT_COMPARATORS[field];
    const keepNaLast = NA_LAST_SORT_FIELDS.includes(field);

    return filtered.sort((a, b) => {
      if (keepNaLast) {
        const naOrder =
          Number(isExecutiveOnlyRole(a)) - Number(isExecutiveOnlyRole(b));
        if (naOrder !== 0) return naOrder;
      }
      return compare(a, b) * multiplier;
    });
  }, [apiData, searchQuery, selectedGrade, selectedRoleFilter, selectedSort]);

  // Get grades from API for filter
  const gradeOptions = useMemo(() => {
    const options = [{ label: "All Grades", value: "all" }];

    if (grades?.items && Array.isArray(grades.items)) {
      const gradeOptionsFromAPI = grades.items.map((item: any) => ({
        label: item.grade_name,
        value: item.grade_name,
      }));
      options.push(...gradeOptionsFromAPI);
    }

    return options;
  }, [grades]);

  const handleOpenDetailsModal = (executive: Executive) => {
    setSelectedExecutive(executive);
    setIsDetailsModalOpen(true);
  };

  const handleOpenAccountsModal = (executive: Executive) => {
    setSelectedExecutive(executive);
    setIsAccountsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    if (!isAccountsModalOpen) {
      setSelectedExecutive(null);
    }
  };

  const handleCloseAccountsModal = () => {
    setIsAccountsModalOpen(false);
    if (!isDetailsModalOpen) {
      setSelectedExecutive(null);
    }
  };

  const getLocationString = (executive: Executive): string => {
    const parts = [];
    if (executive.city) parts.push(executive.city);
    if (executive.country) parts.push(executive.country);
    return parts.length > 0
      ? parts.join(", ")
      : executive.region || "Location not specified";
  };

  const getAccountLoadDisplay = (executive: Executive): string => {
    if (
      executive.performanceScore !== null &&
      executive.performanceScore !== undefined
    ) {
      return executive.performanceScore.toFixed(1);
    }
    return executive.current_accounts?.toString() || "0";
  };

  const getAssignmentLabel = (assignmentType?: string | null) => {
    if (!assignmentType) return "Sponsor";

    const type = assignmentType.toLowerCase();
    if (type === "primary") return "Primary Sponsor";
    if (type === "secondary") return "Secondary Sponsor";

    return `${assignmentType} Sponsor`;
  };

  const getRecentAccounts = (executive: Executive) => {
    if (!executive.accounts || executive.accounts.length === 0) return [];
    // Return last 3 accounts, sorted by assigned_on date (most recent first)
    return [...executive.accounts]
      .sort((a, b) => {
        const dateA = new Date(a.assigned_on).getTime();
        const dateB = new Date(b.assigned_on).getTime();
        return dateB - dateA;
      })
      .slice(0, 3);
  };

  return (
    <div className="bg-white overflow-hidden mt-3">
      <PageHeader title="Executive Staff" icon={Users} />

      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-4">
          {/* Overview Metrics */}
          <div className="grid grid-cols-4 gap-3 mb-5 w-full lg:grid-cols-4 md:grid-cols-4 sm:grid-cols-3 max-sm:grid-cols-2 max-[480px]:grid-cols-1">
            {cardData.map((item, idx) => {
              // Apply color to card value based on label
              const valueColorClass =
                item.label === "Active"
                  ? "text-green-800"
                  : item.label === "Bench"
                    ? "text-blue-800"
                    : "text-[#5371b9]";

              // Apply border color based on label
              const borderClass =
                item.label === "Active"
                  ? "border-green-200"
                  : item.label === "Bench"
                    ? "border-blue-200"
                    : item.label === "Total Executives"
                      ? "border-blue-200"
                      : "border-gray-200";

              return (
                <div
                  key={idx}
                  className={`bg-white rounded-xl shadow-[0_1px_8px_0_rgba(60,72,113,0.07)] px-3.5 py-2.5 flex items-center flex-shrink-0 min-h-[80px] border ${borderClass}`}
                >
                  <div className="w-full">
                    <div
                      className={`text-xl font-medium tracking-[0.03em] min-w-[30px] text-center ml-1 ${valueColorClass}`}
                    >
                      {item.value}
                    </div>
                    <div className="text-gray-600 text-xs font-normal whitespace-nowrap ml-0 text-center mt-1">
                      {item.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Search and Filters */}
          <div className="flex items-center gap-3 mb-6 flex-wrap bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="flex-1 min-w-[280px]">
              <SearchInput
                placeholder="Search executive..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-3 items-center flex-wrap">
              <DropdownSelector
                label="All Roles"
                selected={selectedRoleFilter}
                options={roleFilterOptions}
                onSelect={handleRoleFilterChange}
              />
              <DropdownSelector
                label="All Grades"
                selected={selectedGrade}
                options={gradeOptions}
                onSelect={value =>
                  setSelectedGrade(value === "all" ? null : value)
                }
              />
              <DropdownSelector
                label="Sort By"
                selectedLabelPrefix="Sort by: "
                selected={selectedSort}
                options={EXECUTIVE_SORT_OPTIONS}
                onSelect={setSelectedSort}
              />
            </div>
          </div>

          {/* Executive Cards Grid */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16 bg-red-50 rounded-xl border border-red-200">
              <div className="text-red-600 font-semibold mb-2">
                Error loading executives
              </div>
              <div className="text-red-500 text-sm">{error}</div>
            </div>
          ) : filteredExecutives.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200">
              <div className="text-gray-600 font-medium mb-2">
                No executives found
              </div>
              <div className="text-gray-500 text-sm">
                {searchQuery ||
                selectedGrade ||
                selectedRoleFilter !== ROLE_FILTER_ALL
                  ? "Try adjusting your search or filter criteria"
                  : "No executives available"}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {filteredExecutives.map((executive, index) => {
                const recentAccounts = getRecentAccounts(executive);
                const isLead = isRole(
                  executive.role,
                  ROLES.EXECUTIVE_SPONSOR_LEAD
                );
                const isExecutiveRole = isExecutiveOnlyRole(executive);

                return (
                  <div
                    key={executive.id || `executive-${index}`}
                    className={`bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all border border-gray-200 ${
                      isLead
                        ? "ring-4 ring-amber-400"
                        : isExecutiveRole
                          ? "ring-2 ring-sky-200"
                          : ""
                    }`}
                  >
                    {/* Card Header: amber for Lead, light blue for Executive, purple otherwise */}
                    <div
                      className={`px-4 py-3 text-white bg-gradient-to-r ${
                        isLead
                          ? "from-amber-500 to-yellow-500"
                          : isExecutiveRole
                            ? "from-sky-400 to-blue-500"
                            : "from-purple-600 to-purple-800"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="relative w-8 h-8 rounded-full flex-shrink-0">
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center border-2 border-white/30">
                              <span className="text-white font-semibold text-xs">
                                {getInitials(executive.name)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-2">
                            <h3 className="text-sm font-semibold truncate">
                              {executive.name}
                            </h3>
                            <p className="text-xs opacity-90 truncate">
                              {executive.title || executive.role}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleOpenDetailsModal(executive)}
                            className="text-white hover:text-gray-200 transition-opacity"
                            title="View Executive Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      {/* Basic Info */}
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center text-xs text-gray-600">
                          <MapPin className="h-3 w-3 mr-2 text-gray-500" />
                          <span className="truncate">
                            {getLocationString(executive)}
                          </span>
                        </div>
                        <div className="flex items-center text-xs text-gray-600">
                          <Mail className="h-3 w-3 mr-2 text-gray-500" />
                          <span className="truncate">{executive.email}</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-600">
                          <Phone className="h-3 w-3 mr-2 text-gray-500" />
                          <span className="truncate">
                            {executive.phone || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center text-xs text-gray-600">
                          <Calendar className="h-3 w-3 mr-2 text-gray-500" />
                          <span className="truncate">
                            Joined On:{" "}
                            {executive.joinDate
                              ? formatDateUS(executive.joinDate).split(",")[0]
                              : "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Grade & Lead Badges */}
                      <div className="mb-4 flex items-center gap-2 flex-wrap">
                        {executive.experienceLevel && (
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                              executive.experienceLevel === "E1" ||
                              executive.grade === 1
                                ? "bg-purple-100 text-purple-700"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {executive.experienceLevel}
                          </span>
                        )}
                        {isLead && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-gradient-to-r from-orange-400 to-amber-400 text-white shadow-sm border border-orange-300/30">
                            <Trophy
                              className="w-3.5 h-3.5 text-white shrink-0"
                              strokeWidth={2.5}
                            />
                            LEAD
                          </span>
                        )}
                      </div>

                      {/* Performance Metrics */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="text-center">
                          <div
                            className={`text-xl font-bold ${
                              isExecutiveRole
                                ? "text-gray-500"
                                : "text-orange-500"
                            }`}
                          >
                            {isExecutiveRole
                              ? "NA"
                              : executive.performanceScore?.toFixed(1) ||
                                getAccountLoadDisplay(executive)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Performance
                          </div>
                        </div>
                        <div className="text-center">
                          <div
                            className={`text-xl font-bold ${
                              isExecutiveRole
                                ? "text-gray-500"
                                : "text-green-600"
                            }`}
                          >
                            {isExecutiveRole
                              ? "NA"
                              : `${executive.utilization?.toFixed(0) || 0}%`}
                          </div>
                          <div className="text-xs text-gray-500">
                            Utilization
                          </div>
                        </div>
                      </div>

                      {/* Account Load */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-700">
                            Primary Account Load
                          </span>
                          <span className="text-xs text-gray-500">
                            {isExecutiveRole
                              ? "NA"
                              : `${executive.current_accounts || 0}/${executive.accountLimit || 0}`}
                          </span>
                        </div>
                      </div>

                      {/* Specialties from specialization (comma-separated) */}
                      {(() => {
                        const specialtyChips = String(
                          executive.specialization || ""
                        )
                          .split(",")
                          .map(part => part.trim())
                          .filter(Boolean);

                        if (specialtyChips.length === 0) return null;

                        return (
                          <div className="mb-3">
                            <div className="text-xs font-medium text-gray-700 mb-2">
                              Specialties
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {specialtyChips
                                .slice(0, 3)
                                .map((specialty, idx) => (
                                  <span
                                    key={`${specialty}-${idx}`}
                                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                                  >
                                    {specialty}
                                  </span>
                                ))}
                              {specialtyChips.length > 3 && (
                                <span className="text-xs text-gray-400">
                                  +{specialtyChips.length - 3}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Recent Accounts */}
                      {!isExecutiveRole && recentAccounts.length > 0 && (
                        <div className="mb-3">
                          <div className="text-xs font-medium text-gray-700 mb-2">
                            Recent Accounts
                          </div>
                          <div className="space-y-1">
                            {recentAccounts.slice(0, 2).map((account, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 p-1.5 bg-gray-50 rounded text-xs"
                              >
                                <div className="flex items-center min-w-0 flex-1">
                                  <span className="text-gray-400 mr-1 shrink-0">
                                    🏢
                                  </span>
                                  <span className="truncate">
                                    {account.name}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <span
                                    className={`${tierBadgeLayoutClasses} px-1.5 py-0.5 rounded-md text-[11px] font-semibold ${getTierBadgeClasses(account.tier)}`}
                                    title={
                                      formatTier(account.tier) || undefined
                                    }
                                  >
                                    {formatTier(account.tier) ||
                                      account.tier ||
                                      "N/A"}
                                  </span>
                                  <span className="text-[11px] text-gray-500 whitespace-nowrap">
                                    {getAssignmentLabel(
                                      account.assignment_type as any
                                    )}
                                  </span>
                                </div>
                              </div>
                            ))}
                            {recentAccounts.length > 2 && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleOpenAccountsModal(executive)
                                }
                                className="w-full text-xs text-purple-600 text-center mt-1 hover:underline font-medium"
                              >
                                +{recentAccounts.length - 2} more
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Details & Accounts Modals */}
      {selectedExecutive && (
        <>
          <ExecutiveAccountsModal
            isOpen={isDetailsModalOpen}
            toggle={handleCloseDetailsModal}
            executive={selectedExecutive}
          />
          <AssignedAccountsModal
            isOpen={isAccountsModalOpen}
            toggle={handleCloseAccountsModal}
            executive={selectedExecutive}
          />
        </>
      )}
    </div>
  );
};

export default ExecutiveStaff;
